import styles from "./parametersInput.module.scss";
import {
  FieldValues,
  Path,
  PathValue,
  useController,
  UseControllerProps,
  useWatch
} from "react-hook-form";
import {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { Popup } from "../popup/popup";
import {
  ParametersConstants,
  RequestParametersConstants
} from "../../../constants/parameters.constants";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { SelectedVariable } from "../../../types/variableType";
import { SwitchCheckboxHookForm } from "../switchCheckboxHookForm/switchCheckboxHookForm";
import { ReactComponent as AddSvg } from "../../images/add.svg"
import { AddJsonFieldModal } from "./addJsonFieldModal/addJsonFieldModal";
import { JsonField } from "../../../types/jsonValidationTypes";

type ParametersInputProps<T extends FieldValues> = {
  title: string | JSX.Element;
  params?: Array<{id?: number; name: string}>
  nameJsonValidation?: Path<T>;
  error?: string;
  maxLength?: number;
  showConstantsParameters?: boolean;
  showVariables?: boolean;
  showRequestParameters?: boolean;
} & UseControllerProps<T>

export const ParametersInput = <T extends FieldValues>({ control, nameJsonValidation, title, name, params, error, maxLength, showConstantsParameters, showVariables, showRequestParameters }: ParametersInputProps<T>) => {
  const { t } = useTranslation();

  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const {
    field: { ref, onChange, value, ...inputProps },
  } = useController({
    name,
    control,
    defaultValue: "" as PathValue<T, Path<T>>
  });

  const watchedValue = useWatch({
    control,
    name: nameJsonValidation ?? (undefined as any),
  });
  const jsonValidationWatch = nameJsonValidation ? watchedValue : undefined;


  const endpointInput = useRef<HTMLTextAreaElement>();

  const parametersRef = useRef<HTMLDivElement>(null);
  const variablesRef = useRef<HTMLDivElement>(null);
  const autoGenerateValuesRef = useRef<HTMLDivElement>(null);

  const [customInsert, setCustomInsert] = useState<{ startPos: number, textInsertLength: number }>();

  const [showAddJsonField, setShowAddJsonField] = useState<boolean>(false);
  const [showAddJsonFieldModal, setShowAddJsonFieldModal] = useState<boolean>(false);

  const [showParametersPopup, setShowParametersPopup] = useState<boolean>(false);
  const [showVariablesPopup, setShowVariablesPopup] = useState<boolean>(false);
  const [showAutoGeneratePopup, setShowAutoGeneratePopup] = useState<boolean>(false);

  useEffect(() => {
    if (!!customInsert && endpointInput.current) {
      endpointInput.current.selectionStart = endpointInput.current.selectionEnd = customInsert.startPos + customInsert.textInsertLength;
      endpointInput.current.focus();
      setCustomInsert(undefined);
    }
  }, [value, customInsert, setCustomInsert]);

  useEffect(() => {
    setShowAddJsonField(!!jsonValidationWatch);
  }, [jsonValidationWatch, setShowAddJsonField]);
  
  const onClickAddBlock = (): void => {
    if (!showParametersPopup && !!params?.length) {
      setShowParametersPopup(true);
    }
  }

  const onClickVariables = (): void => {
    if (!showVariablesPopup) {
      setShowVariablesPopup(true);
    }
  }

  const onClickAutoGeneratedValues = useCallback((): void => {
    if (!showAutoGeneratePopup) {
      setShowAutoGeneratePopup(true);
    }
  }, [showAutoGeneratePopup, setShowAutoGeneratePopup])

  const clickParamItem = (indexParam: number) => {
    const input = endpointInput.current;
    if (!!input && !!params) {
      const textToInsert = `{{${params[indexParam].name}}}`;
      addTextToInput(input, textToInsert);
    }
    setShowParametersPopup(false);
  }

  const clickVariableItem = (variable: SelectedVariable) => {
    const input = endpointInput.current;
    if (!!input) {
      addTextToInput(input, `<{${variable.name}}>`);
    }
    setShowVariablesPopup(false);
  }

  const clickConstantParameterItem = (parameter: ParametersConstants | RequestParametersConstants) => {
    const input = endpointInput.current;
    if (!!input) {
      const textToInsert = `<<${parameter}>>`;
      addTextToInput(input, textToInsert);
    }
    setShowAutoGeneratePopup(false);
  }

  const addTextToInput = useCallback((input: HTMLTextAreaElement, textToInsert: string) => {
    const startPos = input.selectionStart;
    const endPos = input.selectionEnd;
    const beforeText = input.value.substring(0, startPos);
    const afterText = input.value.substring(endPos, input.value.length);
    onChange(beforeText + textToInsert + afterText);
    setCustomInsert({ startPos, textInsertLength: textToInsert.length });

  }, [onChange]);

  const onCloseAddJsonFieldModal = useCallback((field?: JsonField) => {
    if (field && endpointInput.current) {
      addTextToInput(endpointInput.current,
        `"${field.name}": "<<${field.required ? "required" : "optional"}:${field.type}${!!field.size ? `:${field.size}` : ""}>>"`)
    }
    setShowAddJsonFieldModal(false);
  }, [setShowAddJsonFieldModal, addTextToInput]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.title}>
          <div className={styles.label}>
            {title}
          </div>
          {nameJsonValidation && (
            <div className={styles.jsonSchemeValidation}>
              {t("parameters.jsonSchemeValidation")}
              <SwitchCheckboxHookForm control={control} name={nameJsonValidation}/>
            </div>
          )}
        </div>
        <div className={`${styles.areaContainer} ${error ? styles.error : ""}`}>
          {(!!params || showConstantsParameters || showVariables || showRequestParameters) && (
            <div className={styles.actions}>
              <div className={styles.mainParameters}>
                {params && (
                  <div className={styles.parametersContainer}
                       ref={parametersRef}
                       onClick={() => onClickAddBlock()}>
                    {t("parameters.parameters")}
                    <Popup
                      parent={parametersRef.current}
                      isShow={showParametersPopup}
                      top={3}
                      classContainer={styles.popupContainer}
                      onClose={() => setShowParametersPopup(false)}
                      child={
                        <>
                          {params.map((param, index) => (
                            <div key={index} className={styles.item} title={param.name}
                                 onClick={() => clickParamItem(index)}>
                              <span>{param.name}</span>
                            </div>
                          ))}
                        </>
                      }/>
                  </div>
                )}
                {!!showVariables && (
                  <div className={styles.parametersContainer}
                       ref={variablesRef}
                       onClick={() => onClickVariables()}>
                    {t("common.variables")}
                    <Popup
                      parent={variablesRef.current}
                      top={3}
                      isShow={showVariablesPopup}
                      classContainer={styles.popupContainer}
                      onClose={() => setShowVariablesPopup(false)}
                      child={
                        <>
                          {selectedVariables.map((variable, index) => (
                            <div key={index}
                                 className={styles.item}
                                 title={variable.name}
                                 onClick={() => clickVariableItem(variable)}>
                              <span>{variable.name}</span>
                            </div>
                          ))}
                        </>
                      }/>
                  </div>
                )}
                {(!!showConstantsParameters || !!showRequestParameters) && !jsonValidationWatch && (
                  <div className={styles.parametersContainer}
                       ref={autoGenerateValuesRef}
                       onClick={() => onClickAutoGeneratedValues()}>
                    {t("parameters.autoGeneratedValues")}
                    <Popup
                      parent={autoGenerateValuesRef.current}
                      top={3}
                      isShow={showAutoGeneratePopup}
                      classContainer={styles.autoGeneratePopup}
                      onClose={() => setShowAutoGeneratePopup(false)}
                      child={
                        <>
                          {!!showConstantsParameters && Object.values(ParametersConstants).map((parameter) => (
                            <div key={parameter}
                                 className={styles.parameterItem}
                                 onClick={() => clickConstantParameterItem(parameter)}>
                              {t(`parameters.${parameter}`)}
                            </div>
                          ))}
                          {!!showRequestParameters && Object.values(RequestParametersConstants).map((parameter) => (
                            <div key={parameter}
                                 className={styles.parameterItem}
                                 onClick={() => clickConstantParameterItem(parameter)}>
                              {parameter}
                            </div>
                          ))}
                        </>
                      }/>
                  </div>
                )}
              </div>
              {showAddJsonField && (
                <div className={styles.addJsonField}
                     onClick={() => setShowAddJsonFieldModal(true)}>
                  <AddSvg/>
                  {t("parameters.addJsonField")}
                </div>
              )}
            </div>
          )}
          <textarea maxLength={maxLength}
                    {...inputProps}
                    onChange={onChange}
                    value={value}
                    ref={(e) => {
                      ref(e);
                      endpointInput.current = e || undefined;
                    }}
          />
        </div>

        {error && <p className={styles.errorMessage}> {error}</p>}
      </div>

      {showAddJsonFieldModal && (
        <AddJsonFieldModal onClose={onCloseAddJsonFieldModal}/>
      )}

    </>
  )
}
